import { API_BASE_URL } from '../../config';

// API Configuration
export const API_CONFIG = {
  BASE_URL: API_BASE_URL,
  TIMEOUT: 360000, // 360 seconds (6 minutes) for LLM operations - allows for 3-5 min Krybot responses with buffer
  CONTENT_TYPE: 'application/json',
};

// API Endpoints
export const API_ENDPOINTS = {
  // Health
  HEALTH: '/health/health',

  // Users (auth endpoints)
  USERS: {
    BASE: '/users',
    BY_ID: (id: string) => `/users/${id}`,
    ME: '/users/me',
    COMPANIES: (id: string) => `/users/${id}/companies`,
    ADD_TO_COMPANY: (userId: string, companyId: string) => `/users/${userId}/companies/${companyId}`,
    PERMISSIONS: (id: string) => `/users/${id}/permissions`,
    ACTIVITY: (id: string) => `/users/${id}/activity`,
    PREFERENCES: (id: string) => `/users/${id}/preferences`,
    ROLE: (id: string) => `/users/${id}/role`,
  },

  // Dev Users (no auth required for demo)
  DEV_USERS: {
    BASE: '/dev/users',
    BY_ID: (id: string) => `/dev/users/${id}`,
    ME: '/dev/users/me',
  },

  // Chat Threads (mounted under /chat in backend, chat.py)
  THREADS: {
    BASE: '/chat/threads',
    BY_ID: (id: string) => `/chat/threads/${id}`,
    BY_USER: (userId: string) => `/chat/threads?user_id=${userId}`,
    MESSAGES: (threadId: string) => `/chat/threads/${threadId}/messages`,
    CONTEXT: (threadId: string) => `/chat/threads/${threadId}/context`,
    ASSIGN: '/chat/threads/assign',
  },

  // Commands
  COMMANDS: {
    BASE: '/commands',
    EXECUTE: '/commands/execute',
    HISTORY: (userId: string) => `/commands/history?user_id=${userId}`,
  },

  // Pinned Artifacts
  PINNED_ARTIFACTS: {
    BASE: '/commands/pinned',
    BY_ID: (id: string) => `/commands/pinned/${id}`,
    ACCESS: (id: string) => `/commands/pinned/${id}/access`,
    THREAD_PINNED: (threadId: string) => `/chat/threads/${threadId}/pinned-artifacts`,
  },

  // Artifact Types
  ARTIFACT_TYPES: {
    BASE: '/artifact-types',
    BY_ID: (id: string) => `/artifact-types/${id}`,
  },

  // Artifacts
  ARTIFACTS: {
    BASE: '/artifacts',
    BY_ID: (id: string) => `/artifacts/${id}`,
    PARAMETERS: (artifact_id: string) => `/artifacts/${artifact_id}/parameters`,
    HIERARCHY: '/artifacts/hierarchy',
    ROOTS: '/artifacts/roots',
    TREES: '/artifacts/trees',
    TREE_ARTIFACTS: (tree_id: string) => `/artifacts/tree/${tree_id}/hierarchy`,
    BY_BUSINESS_MODEL: (tree_id: string, business_model_id: string) =>
      `/artifacts/tree/${tree_id}/business-model/${business_model_id}`,
    RECOMMENDED_CHILD_ARTIFACTS: (id: string) =>
      `/artifacts/recommendations/artifacts/${id}/recommended-child-artifacts`,
    RECOMMENDED_PROCESS_ARTIFACTS: (id: string) =>
      `/artifacts/recommendations/artifacts/${id}/recommended-process-artifacts`,
    RECOMMENDED_STATES: (id: string) =>
      `/artifacts/recommendations/artifacts/${id}/recommended-states`,
    RECOMMENDED_TRANSITIONS: (id: string) =>
      `/artifacts/recommendations/artifacts/${id}/recommended-transitions`,
    RECOMMENDED_PROCESSES: (id: string) =>
      `/artifacts/recommendations/artifacts/${id}/recommended-processes`,
    RECOMMENDED_PEER_ARTIFACTS: (id: string) =>
      `/artifacts/recommendations/artifacts/${id}/recommended-peer-artifacts`,
    TRANSITIONS: (artifact_id: string) => `/artifacts/${artifact_id}/transitions`,
    UPDATE_TRANSITION: (artifact_id: string, transition_id: string) =>
      `/artifacts/${artifact_id}/transitions/${transition_id}`,
    DELETE_TRANSITION: (artifact_id: string, transition_id: string) =>
      `/artifacts/${artifact_id}/transitions/${transition_id}`,
    STATES: (artifact_id: string) => `/artifacts/${artifact_id}/states`,
  },

  // Artifact Items
  ARTIFACT_ITEMS: {
    BASE: '/artifact-items',
    BY_ID: (id: string) => `/artifact-items/${id}`,
    PROPERTIES: (id: string) => `/artifact-items/${id}/properties`,
    REQUIRING_USER_INPUT: '/artifact-items/requiring-user-input',
    CREATE_WITH_STATE: '/artifact-items', // POST to create item with state
    CURRENT_STATE: (id: string) => `/artifact-items/${id}/current-state`,
    STATE_HISTORY: (id: string) => `/artifact-items/${id}/state-history`,
    BY_ARTIFACT: (artifactId: string) => `/artifacts/${artifactId}/items`,
  },

  // Artifact Trees
  ARTIFACT_TREES: {
    BASE: '/artifact-trees',
    ROOT_STATUS: '/artifact-trees/root-status',
  },

  // Prime Artifacts
  PRIME_ARTIFACTS: {
    BASE: '/prime-artifacts',
    BY_ID: (id: string) => `/prime-artifacts/${id}`,
    STATES: (id: string) => `/prime-artifacts/${id}/states`,
    STATE_VIEW: (artifactId: string, stateId: string) =>
      `/prime-artifacts/${artifactId}/states/${stateId}/view`,
    TRANSITIONS: (id: string) => `/prime-artifacts/${id}/transitions`,
    SEARCH: (name: string) => `/prime-artifacts/search?name=${name}`,
    HIERARCHY: '/prime-artifacts/hierarchy/tree',
    UPDATE_ARTIFACT: (id: string) => `/prime-artifacts/${id}`,
    PARAMETERS: (id: string) => `/prime-artifact-parameters/artifact/${id}`,
    DEPENDENCIES: (id: string) => `/prime-artifacts/${id}/dependencies`,
    WITH_PARAMETERS: (id: string) => `/prime-artifacts/${id}/with-parameters`,
    ROOTS: '/prime-artifacts/roots',
    UPDATE_STATE: (artifactId: string, stateId: string) =>
      `/prime-artifacts/${artifactId}/states/${stateId}`,
    DELETE_STATE: (artifactId: string, stateId: string) =>
      `/prime-artifacts/${artifactId}/states/${stateId}`,
    UPDATE_TRANSITION: (artifactId: string, transitionId: string) =>
      `/prime-artifacts/${artifactId}/transitions/${transitionId}`,
    DELETE_TRANSITION: (artifactId: string, transitionId: string) =>
      `/prime-artifacts/${artifactId}/transitions/${transitionId}`,
    // ISSUE-023: Apply state template endpoint
    APPLY_STATE_TEMPLATE: (id: string) => `/prime-artifacts/${id}/apply-state-template`,
  },

  // Artifact Relationships
  ARTIFACT_RELATIONSHIPS: {
    BASE: '/artifact-relationships',
    BY_ID: (id: string) => `/artifact-relationships/${id}`,
  },

  // Prime Artifact Transitions
  PRIME_ARTIFACT_TRANSITIONS: {
    BASE: '/prime-artifacts',
    BY_ARTIFACT: (artifactId: string) => `/prime-artifacts/${artifactId}/transitions`,
    BY_PROCESS: (processId: string) => `/prime-processes/${processId}/transitions`,
    CREATE: (artifactId: string) => `/prime-artifacts/${artifactId}/transitions`,
    UPDATE: (artifactId: string, transitionId: string) =>
      `/prime-artifacts/${artifactId}/transitions/${transitionId}`,
    DELETE: (artifactId: string, transitionId: string) =>
      `/prime-artifacts/${artifactId}/transitions/${transitionId}`,
  },

  // Prime Processes
  PRIME_PROCESSES: {
    BASE: '/prime-processes',
    BY_ARTIFACT: (artifactId: string) => `/prime-artifacts/${artifactId}/processes`,
    BY_ID: (id: string) => `/prime-processes/${id}`,
    PROCESS_VIEW: (processId: string) => `/prime-processes/${processId}/view`,
    SEARCH: (name: string) => `/prime-processes/search?name=${name}`,
    TRANSITIONS: (id: string) => `/prime-processes/${id}/transitions`,
  },

  // Dashboard
  DASHBOARD_PROCESS_SUMMARY: '/dashboard/process-summary',
  DASHBOARD_PROCESS_TYPE_SUMMARY: '/dashboard/process-type/{process_type}',
  DASHBOARD_PROCESS_ITEMS: '/dashboard/process/{process_id}/items',
  DASHBOARD_PROCESS_TYPES: '/dashboard/process-types',
  DASHBOARD_METRICS: '/dashboard/dashboard-metrics',

  // Migration
  MIGRATION: {
    BASE: '/relationship-migration',
    MIGRATE: '/relationship-migration/migrate',
  },

  // Prime Artifact States
  PRIME_ARTIFACT_STATES: {
    BASE: '/prime-artifact-states',
    BY_ARTIFACT: (artifactId: string) => `/prime-artifacts/states/${artifactId}`,
    STATE: (stateId: string) => `/prime-artifacts/state/${stateId}`,
  },

  // Parameter Types
  PARAMETER_TYPES: {
    BASE: '/parameter-types',
    BY_ID: (id: string) => `/parameter-types/${id}`,
  },

  // Prime Artifact Parameters
  PRIME_ARTIFACT_PARAMETERS: {
    BASE: '/prime-artifact-parameters',
    BY_ID: (id: string) => `/prime-artifact-parameters/${id}`,
    BY_ARTIFACT: (artifactId: string) => `/prime-artifact-parameters/artifact/${artifactId}`,
  },

  // GPT Integration
  GPT_INTEGRATION: {
    BASE: '/gpt',
    QUERY: '/gpt/query',
    GENERATE_ARTIFACT: '/gpt/generate/artifact',
    GENERATE_STATES: '/gpt/generate/states',
    GENERATE_TRANSITIONS: '/gpt/generate/transitions',
    GENERATE_PROCESSES: '/gpt/generate/processes',
    GENERATE_COMPLETE_MODEL: '/gpt/generate/complete-model',
    GENERATE_FOLLOWUP: '/gpt/generate/followup',
    PROCESS_RESPONSE: '/gpt/process',
    CHECK_DUPLICATES: '/gpt/check-duplicates',
  },

  // Artifact Processes
  ARTIFACT_PROCESSES: {
    BASE: '/processes',
    BY_ID: (id: string) => `/processes/${id}`,
    BY_ARTIFACT: (artifactId: string) => `/artifacts/${artifactId}/processes`,
    SEARCH: (name: string) => `/processes/search?name=${name}`,
  },

  // Prime to Company Import
  PRIME_TO_COMPANY_IMPORT: {
    BASE: '/prime-to-company-import',
    IMPORT: '/prime-to-company-import',
  },

  // Companies
  COMPANIES: {
    BASE: '/crud/companies',
    BY_ID: (id: string) => `/crud/companies/${id}`,
    PERMISSIONS: (id: string) => `/companies/${id}/permissions`,
    STATS: (id: string) => `/crud/companies/${id}/stats`,
    BILLING: (id: string) => `/crud/companies/${id}/billing`,
    PLATFORM_INITIALIZATION: (companyId: string) => `/companies/${companyId}/platform-initialization`,
    /** GET aggregate business control bridge (auth + company access); aligns with `company_data` router */
    CONTROL_BRIDGE: (companyId: string) => `/companies/${companyId}/control-bridge`,
  },

  // Permissions
  PERMISSIONS: {
    BASE: '/permissions',
    CHECK: '/users/check-permission',
  },

  // Roles
  ROLES: {
    BASE: '/roles',
  },

  // Company Instances
  COMPANY_INSTANCES: {
    BASE: '/company-instances',
    BY_ID: (id: string) => `/company-instances/${id}`,
    ARTIFACTS: (id: string) => `/company-instances/${id}/artifacts`,
    USERS: (id: string) => `/company-instances/${id}/users`,
    INVITE: (id: string) => `/company-instances/${id}/invite-user`,
    SEARCH: (term: string) => `/company-instances/search?search_term=${term}`,
    /** Item state history - backend uses /artifact-items/{item_id}/state-history (company-instances has no items/history route) */
    ITEM_HISTORY: (itemId: string) => `/artifact-items/${itemId}/state-history`,
  },

  // Business Models
  BUSINESS_MODELS: {
    BASE: '/business-models',
    BY_ID: (id: string) => `/business-models/${id}`,
    BY_TREE: (treeId: string) => `/business-models/tree/${treeId}`,
    ACTIVE_BY_TREE: (treeId: string) => `/business-models/tree/${treeId}/active`,
    COMPONENTS: (modelId: string) => `/business-models/${modelId}/components`,
    COMPONENT_BY_ID: (modelId: string, componentId: string) => `/business-models/${modelId}/components/${componentId}`,
    TYPES: '/business-models/types',
  },

  // Subscriptions
  SUBSCRIPTIONS: {
    BASE: '/subscriptions',
    BY_ID: (id: string) => `/subscriptions/${id}`,
    TRANSITION: (id: string) => `/subscriptions/${id}/transition`,
    VALID_TRANSITIONS: (id: string) => `/subscriptions/${id}/valid-transitions`,
    ASSIGN_USER: (id: string) => `/subscriptions/${id}/assign-user`,
    UNASSIGN_USER: (id: string, userId: string) => `/subscriptions/${id}/assign-user/${userId}`,
    /** Seat list for a company subscription (layer C). */
    SUBSCRIPTION_USERS: (id: string) => `/subscriptions/${id}/users`,
    // Company ownership uses CREATE_COMPANY_SUBSCRIPTION / COMPANY_SUBSCRIPTION_BY_ID (not assign-company).
    ARTIFACT_ITEM: (id: string) => `/subscriptions/${id}/artifact-item`,
    START_BUSINESS_CORE: '/subscriptions/krysalis-business-core/start',
    // User subscription endpoints
    USER_SUBSCRIPTIONS: (userId: string) => `/subscriptions/users/${userId}/subscriptions`,
    USER_SUBSCRIPTION_BY_ID: (userId: string, subscriptionId: string) => `/subscriptions/users/${userId}/subscriptions/${subscriptionId}`,
    CREATE_USER_SUBSCRIPTION: '/subscriptions/users',
    // Company subscription endpoints
    COMPANY_SUBSCRIPTIONS: (companyId: string) => `/subscriptions/companies/${companyId}/subscriptions`,
    COMPANY_SUBSCRIPTION_BY_ID: (companyId: string, subscriptionId: string) => `/subscriptions/companies/${companyId}/subscriptions/${subscriptionId}`,
    CREATE_COMPANY_SUBSCRIPTION: '/subscriptions/companies',
    // Bootstrap management
    BOOTSTRAP_COMPANY: (companyId: string) => `/subscriptions/bootstrap/company/${companyId}`,
    // Lifecycle management
    LIFECYCLE: {
      PLACE_ORDER: '/subscriptions/lifecycle/place-order',
      PROVISION: (id: string) => `/subscriptions/lifecycle/provision/${id}`,
      ACTIVATE: (id: string) => `/subscriptions/lifecycle/activate/${id}`,
      ONBOARD: (id: string) => `/subscriptions/lifecycle/onboard/${id}`,
      COMPLETE: '/subscriptions/lifecycle/complete',
    },
    // Trial management
    TRIALS: {
      CREATE: '/subscriptions/trials/create',
      CONVERT: (id: string) => `/subscriptions/trials/convert/${id}`,
      LIMITATIONS: (id: string) => `/subscriptions/trials/limitations/${id}`,
    },
    // Expiration management
    EXPIRATION: {
      EXPIRING_SOON: '/subscriptions/expiration/expiring-soon',
      PROCESS: '/subscriptions/expiration/process',
    },
    // Renewal management
    RENEWAL: {
      PROCESS: '/subscriptions/renewal/process',
      RENEW_USER: (id: string) => `/subscriptions/renewal/renew-user/${id}`,
      RENEW_COMPANY: (id: string) => `/subscriptions/renewal/renew-company/${id}`,
    },
    // Feature checking
    FEATURE_CHECK: {
      USER: (userId: string, featureCode: string) => `/subscriptions/users/${userId}/check/${featureCode}`,
      COMPANY: (companyId: string, featureCode: string) => `/subscriptions/companies/${companyId}/check/${featureCode}`,
      CHECK: '/subscriptions/check',
    },
    // State machine
    STATE_MACHINE: {
      TRANSITION: (id: string) => `/subscriptions/state-machine/transition/${id}`,
      VALID_TRANSITIONS: (id: string) => `/subscriptions/state-machine/valid-transitions/${id}`,
    },
    // Usage and status
    USAGE: {
      ARTIFACT_ITEM: (id: string) => `/subscriptions/${id}/artifact-item`,
      STATE_HISTORY: (id: string) => `/subscriptions/${id}/state-history`,
      SYNC_STATUS: (id: string) => `/subscriptions/${id}/sync-status`,
      USAGE_EVENTS: (id: string) => `/subscriptions/${id}/usage-events`,
      TRANSITION_TO_IN_USE: (id: string) => `/subscriptions/${id}/transition-to-in-use`,
      IS_IN_USE: (id: string) => `/subscriptions/${id}/is-in-use`,
    },
  },

  // Subscription Types
  SUBSCRIPTION_TYPES: {
    BASE: '/subscriptions/types',
    BY_ID: (id: string) => `/subscriptions/types/${id}`,
    BY_CODE: (code: string) => `/subscriptions/types/code/${code}`,
    CREATE: '/subscriptions/types',
    UPDATE: (id: string) => `/subscriptions/types/${id}`,
    DELETE: (id: string) => `/subscriptions/types/${id}`,
  },

  // Features
  FEATURES: {
    BASE: '/subscriptions/features',
    BY_ID: (id: string) => `/subscriptions/features/${id}`,
    BY_CODE: (code: string) => `/subscriptions/features/code/${code}`,
    CREATE: '/subscriptions/features',
    UPDATE: (id: string) => `/subscriptions/features/${id}`,
    DELETE: (id: string) => `/subscriptions/features/${id}`,
  },

  // Feature Access (subscription router — not top-level /users)
  FEATURE_ACCESS: {
    USER_FEATURES: (userId: string) => `/subscriptions/users/${userId}/features`,
    CHECK_FEATURE: (userId: string, featureCode: string) =>
      `/subscriptions/users/${userId}/check/${featureCode}`,
  },

  // Sessions (ISSUE-219: lifecycle + IntentML snapshot — sessions.py, not bridge-sessions)
  SESSIONS: {
    COMPLETE: (sessionId: string) => `/sessions/${sessionId}/complete`,
    INTENTML_DOWNLOAD: (sessionId: string) => `/sessions/${sessionId}/intentml-download`,
  },

  // Bridge Sessions (session-scoped, bridge_sessions.py) - plural
  BRIDGE_SESSIONS: {
    STATE: (sessionId: string) => `/bridge-sessions/${sessionId}/state`,
    DOMAIN_HIERARCHY: (sessionId: string) => `/bridge-sessions/${sessionId}/domain-hierarchy`,
    DOMAIN_COMPLETE: (sessionId: string, domainId: string) =>
      `/bridge-sessions/${sessionId}/domains/${domainId}/complete`,
    ENTROPY_SCORES: (sessionId: string) => `/bridge-sessions/${sessionId}/entropy-scores`,
    ENTROPY_MAP: (sessionId: string) => `/bridge-sessions/${sessionId}/entropy-map`,
    OBJECTIVES: (sessionId: string) => `/bridge-sessions/${sessionId}/objectives`,
  },

  // Objectives (bridge card from objective artifact_item)
  OBJECTIVES: {
    BRIDGE_CARD: (objectiveArtifactItemId: string) => `/objectives/${objectiveArtifactItemId}/bridge-card`,
  },

  // Bridge Session (company-scoped, bridge_session.py) - singular
  BRIDGE_SESSION: {
    CREATE: (companyId: string) => `/bridge-session/${companyId}/create`,
    SCOPE: (companyId: string, objectiveId: string) =>
      `/bridge-session/${companyId}/objectives/${objectiveId}/scope`,
    VALIDATE_SCOPE: (companyId: string) => `/bridge-session/${companyId}/validate-scope`,
  },

  // State Machine (state_machine_analysis.py has POST /state-machines/{id}/validate)
  STATE_MACHINE_VALIDATE: (artifactId: string) => `/state-machines/${artifactId}/validate`,

  // State Machine Reporting
  STATE_MACHINE: {
    EXECUTION_HISTORY: (artifactId: string) => `/artifacts/${artifactId}/execution/history`,
    STATE_HISTORY: (artifactId: string) => `/artifacts/${artifactId}/state/history`,
    TRANSITION_HISTORY: (artifactId: string) => `/artifacts/${artifactId}/transition/history`,
    VALIDATE_WORKFLOW: (artifactId: string) => `/artifacts/${artifactId}/workflow/validate`,
    WORKFLOW_PROGRESS: (artifactId: string) => `/workflow/artifacts/${artifactId}/progress`,
    WORKFLOW_ANALYTICS: (artifactId: string) => `/artifacts/${artifactId}/workflow/analytics`,
    STATE_DISTRIBUTION: (artifactId: string) => `/artifacts/${artifactId}/state/distribution`,
    ARTIFACT_STATE_SUMMARY: (artifactId: string) => `/artifacts/${artifactId}/state/summary`,
    ARTIFACT_ITEM_SUMMARY: (artifactId: string) => `/artifacts/${artifactId}/items/summary`,
    STATE_BY_ID: (stateId: string) => `/states/${stateId}`,
    TRANSITION_BY_ID: (transitionId: string) => `/transitions/${transitionId}`,
  },

  // Dashboard Reporting
  DASHBOARD_REPORTING: {
    COMPANY_STATE_SUMMARY: (companyId: string) => `/dashboard/companies/${companyId}/state-summary`,
    COMPANY_ITEM_SUMMARY: (companyId: string) => `/dashboard/companies/${companyId}/item-summary`,
    COMPANY_WORKFLOW_METRICS: (companyId: string) => `/dashboard/companies/${companyId}/workflow-metrics`,
    TREE_STATE_SUMMARY: (treeId: string) => `/dashboard/trees/${treeId}/state-summary`,
    TREE_ITEM_SUMMARY: (treeId: string) => `/dashboard/trees/${treeId}/item-summary`,
    DASHBOARD_METRICS: (companyId: string) => `/dashboard/companies/${companyId}/metrics`,
    ITEMS_REQUIRING_ATTENTION: (companyId: string) => `/dashboard/companies/${companyId}/items-requiring-attention`,
  },

  // Financial Projections
  FINANCIAL_PROJECTIONS: {
    BASE: '/financial-projections',
    BY_ID: (id: string) => `/financial-projections/${id}`,
    BY_COMPANY: (companyId: string) => `/financial-projections/company/${companyId}`,
    RUN: (id: string) => `/financial-projections/${id}/run`,
    VALIDATE: (id: string) => `/financial-projections/${id}/validate`,
    RESULTS: (id: string) => `/financial-projections/${id}/results`,
    PARAMETERS: {
      BASE: '/financial-projections/parameters',
      BY_ID: (id: string) => `/financial-projections/parameters/${id}`,
      BY_COMPANY: (companyId: string) => `/financial-projections/parameters/company/${companyId}`,
      TYPES: '/financial-projections/parameters/types',
    },
    DISTRIBUTION_TYPES: '/financial-projections/distributions/types',
  },

  // Krybot Agents
  KRYBOT: {
    BASE: '/krybot',
    AGENTS: {
      BASE: '/krybot/agents',
      BY_ID: (id: string) => `/krybot/agents/${id}`,
      VERSIONS: (id: string) => `/krybot/agents/${id}/versions`,
      ACTIVATE: (id: string) => `/krybot/agents/${id}/activate`,
      RUNS: {
        BASE: '/krybot/agents/runs',
        BY_ID: (runId: string) => `/krybot/agents/runs/${runId}`,
        CANCEL: (runId: string) => `/krybot/agents/runs/${runId}/cancel`,
        RESUBMIT: (runId: string) => `/krybot/agents/runs/${runId}/resubmit`,
        RESPOND: (runId: string) => `/krybot/agents/runs/${runId}/respond`,
      },
      START_RUN: (agentId: string) => `/krybot/agents/${agentId}/runs`,
    },
    APPROVALS: {
      BASE: '/krybot/approvals',
      BY_ID: (id: string) => `/krybot/approvals/${id}`,
      DECISION: (id: string) => `/krybot/approvals/${id}/decision`,
      ASSIGN: (id: string) => `/krybot/approvals/${id}/assign`,
      HISTORY: (id: string) => `/krybot/approvals/${id}/history`,
    },
    SKILL_CONTENT: (skillBundle: string) =>
      `/krybot/skill-content/${encodeURIComponent(skillBundle)}`,
    CHAT: {
      BOOTSTRAP: '/krybot/chat/threads/bootstrap',
      THREADS: '/krybot/chat/threads',
      RESUME: '/krybot/chat/threads/resume',
      SWEEP_EMPTY: '/krybot/chat/threads/sweep-empty',
      MESSAGES: (threadId: string) => `/krybot/chat/threads/${threadId}/messages`,
      HISTORY: (threadId: string) => `/krybot/chat/threads/${threadId}/history`,
      HANDOFF: (threadId: string) => `/krybot/chat/threads/${threadId}/handoff`,
      AGENT: (threadId: string) => `/krybot/chat/threads/${threadId}/agent`,
      REFRESH_CONTEXT: (threadId: string) => `/krybot/chat/threads/${threadId}/refresh-context`,
      ATTACHMENTS: (threadId: string) => `/krybot/chat/threads/${threadId}/attachments`,
      ATTACHMENT_BY_ID: (threadId: string, attachmentId: string) =>
        `/krybot/chat/threads/${threadId}/attachments/${attachmentId}`,
      ATTACHMENT_ROUTE_EXTRACTION: (threadId: string, attachmentId: string) =>
        `/krybot/chat/threads/${threadId}/attachments/${attachmentId}/route-extraction`,
      PERSON_DELIVER: (threadId: string) => `/krybot/chat/threads/${threadId}/messages/person-deliver`,
      PROMPT_BUILDER: '/krybot/chat/prompt-builder',
    },
    TEMPLATES: {
      BASE: '/krybot/templates',
      BY_KEY: (key: string) => `/krybot/templates/${key}`,
      VERSIONS: (key: string) => `/krybot/templates/${key}/versions`,
      RENDER: (key: string) => `/krybot/templates/${key}/render`,
    },
  },

  // Skills
  SKILLS: {
    BASE: '/skills',
    BY_ID: (id: string) => `/skills/${id}`,
    TREE: '/skills/tree',
    HIERARCHY: (id: string) => `/skills/${id}/hierarchy`,
    CHILDREN: (id: string) => `/skills/${id}/children`,
    MATCH: '/skills/match',
    USERS: (skillId: string) => `/skills/${skillId}/users`,
    USER_ASSIGNMENT: (skillId: string, userId: string) => `/skills/${skillId}/users/${userId}`,
    IMPORT: '/skills/import',
    IMPORT_FILE: '/skills/import-file',
  },

  // Process Flows
  PROCESS_FLOWS: {
    BASE: '/process-flows',
    BY_ID: (id: string) => `/process-flows/${id}`,
    STEPS: (flowId: string) => `/process-flows/${flowId}/steps`,
    STEP_BY_ID: (flowId: string, stepId: string) => `/process-flows/${flowId}/steps/${stepId}`,
    EXECUTE: (flowId: string) => `/process-flows/${flowId}/execute`,
    EXECUTIONS: (flowId: string) => `/process-flows/${flowId}/executions`,
    COMPLETE_EXECUTION: (flowId: string, executionId: string) => `/process-flows/${flowId}/executions/${executionId}/complete`,
    ANALYTICS: '/process-flows/analytics',
    PERFORMANCE: (flowId: string) => `/process-flows/${flowId}/performance`,
  },

  // Projections (ISSUE-006)
  PROJECTIONS: {
    START: '/projections/start',
    ARTIFACT_ITEMS: (id: string) => `/projections/${id}/artifact-items`,
    TIMELINE: (id: string) => `/projections/${id}/timeline`,
    STOP: (id: string) => `/projections/${id}/stop`,
  },
};

// Helper function to build API URLs
export const buildApiUrl = (endpoint: string): string => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};
